// Components
import { Typography } from '@archieai/ui';
import { BlogCard } from '../BlogCard/BlogCard';

// Motion
import { motion } from 'motion/react';

// Utils
import { cn } from '../../utils/cn';

// Types
type RelatedArticle = {
  id: string;
  imageSrc: string;
  imageAlt?: string;
  title: string;
  excerpt: string;
  author: string;
  tags: string;
  href: string;
};

type RelatedArticlesProps = {
  /** Title for the section */
  title?: string;
  /** Array of related articles to display */
  articles: RelatedArticle[];
  /** Additional className for custom styling */
  className?: string;
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 30,
    },
  },
};

/**
 * RelatedArticles - Section displaying related article cards
 * Used at the bottom of article detail pages
 */
export const RelatedArticles = ({
  title = 'Related Articles',
  articles,
  className,
}: RelatedArticlesProps) => {
  if (!articles || articles.length === 0) return null;

  return (
    <section
      className={cn(
        'flex flex-col items-center gap-[var(--spacing-32)] w-full',
        className
      )}
    >
      {/* Section Title */}
      <Typography as="h2" variant="h1" className="text-blue-1000 text-center">
        {title}
      </Typography>

      {/* Cards Grid */}
      <motion.div
        className="grid grid-cols-3 gap-[var(--spacing-24)] w-full max-lg:grid-cols-2 max-md:grid-cols-1"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        {articles.slice(0, 3).map((article) => (
          <motion.div key={article.id} variants={cardVariants}>
            <BlogCard
              imageSrc={article.imageSrc}
              imageAlt={article.imageAlt}
              title={article.title}
              excerpt={article.excerpt}
              author={article.author}
              tags={article.tags}
              href={article.href}
              className="max-w-none"
            />
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

